import React from 'react';
import { useParams } from 'react-router-dom';

import { UxContext } from '@client/context/UxContext';
import useLediumPage from '@client/hooks/useLediumPage';
import { useGlobalStore } from '@client/store/globalStore/context/globalContext';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import aggregateSpectrograms from '@client/utils/aggregateSpectrograms';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { LightChannelDoc, SpectralRanges, SpectrogramPoint } from '@ledium/types';
import { calculateRelativeSpectrogramRanges } from '@ledium-mec/shared-utils';
import { Alert } from '@mui/material';

import { MixerChannel } from './Components/SpectrumMixerChannelSettingsItem';
import useCreateLightRecipeFromSpectrum from './Hooks/useCreateLightRecipeFromSpectrum';
import SpectrumMixerView from './SpectrumMixerView';

const SpectrumMixer = () => {
    const {
        globalState: { selectedGroupId },
        globalActions
    } = useGlobalStore();
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const { pageType } = useParams<{ pageType?: string }>();
    const lightChannels = formData.lightChannels as MixerChannel[];
    const lightRecipe = formData.lightRecipe as { id?: string; spectrogram?: SpectrogramPoint[] } | null;

    const { handleAlert } = React.useContext(UxContext)!;
    const { findOrCreate, assignRecipe } = useCreateLightRecipeFromSpectrum();
    const pendingLightRecipeIdRef = React.useRef<string | undefined>(undefined);

    const [groupHasNoChannels, setGroupHasNoChannels] = React.useState(false);
    const [channelsLoaded, setChannelsLoaded] = React.useState(false);
    const needsBaselineResetRef = React.useRef(false);

    const { mainMenu, onSave } = useLediumPage({
        newItemDefaults: { zoneId: selectedGroupId },
        saveDataMap: () => ({
            name: formData.name,
            description: formData.description,
            zoneId: formData.zoneId,
            lightChannels: lightChannels.map((lightChannel) => ({
                id: lightChannel.id,
                intensity: lightChannel.intensity,
                intensityPercent: lightChannel.intensityPercent,
                intensityRanges: lightChannel.intensityRanges
            })),
            lightRecipeId: pendingLightRecipeIdRef.current ?? lightRecipe?.id
        })
    });

    React.useEffect(() => {
        if (!formData.loaded) {
            return;
        }
        if (pageType === 'new') {
            async function loadGroupChannels() {
                const channels = await lediumApi.get<LightChannelDoc[]>('lightChannels');
                if (!channels) {
                    setChannelsLoaded(true);
                    return;
                }
                const groupChannels = channels.filter((ch) => ch.zoneId === selectedGroupId);
                if (groupChannels.length) {
                    const mixerChannels: MixerChannel[] = groupChannels.map((ch) => ({
                        ...ch,
                        id: ch._id,
                        intensityPercent: 0,
                        intensity: 0,
                        intensityRanges: calculateRelativeSpectrogramRanges(ch.spectrogramRanges, 0)
                    }));
                    pageActions.formData.updateFormItem({ lightChannels: mixerChannels });
                    pageActions.formData.setFormBaseline();
                } else {
                    setGroupHasNoChannels(true);
                }
                setChannelsLoaded(true);
            }
            loadGroupChannels();
        } else {
            if (formData.zoneId && formData.zoneId !== selectedGroupId) {
                globalActions.zones.setSelectedGroup(formData.zoneId as string);
            }
            needsBaselineResetRef.current = true;
            setChannelsLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.loaded]);

    React.useEffect(() => {
        const intensityRanges: Record<string, Record<string, number>> = {};
        for (const unit of ['pfd', 'irradiance'] as const) {
            intensityRanges[unit] = {};
            if (lightChannels) {
                lightChannels.forEach((channel) => {
                    const rangeUnit = channel.intensityRanges[unit];
                    if (!rangeUnit) {
                        return;
                    }
                    for (const category of Object.keys(rangeUnit) as (keyof SpectralRanges)[]) {
                        if (intensityRanges[unit][category] === undefined) {
                            intensityRanges[unit][category] = 0;
                        }
                        intensityRanges[unit][category] += rangeUnit[category];
                    }
                });
            }
        }
        pageActions.formData.updateFormItem({ intensityRanges });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.lightChannels]);

    React.useEffect(() => {
        if (lightChannels) {
            const aggregatedSpectrogram = aggregateSpectrograms(lightChannels, lightRecipe);
            pageActions.formData.updateFormItem({ aggregatedSpectrogram });
            if (needsBaselineResetRef.current) {
                needsBaselineResetRef.current = false;
                pageActions.formData.setFormBaseline();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.lightChannels, formData.lightRecipe]);

    const aggregatedSpectrogram = formData.aggregatedSpectrogram as SpectrogramPoint[] | undefined;

    async function handleSave() {
        const hasData = aggregatedSpectrogram?.some((p) => p.irradiance > 0) ?? false;

        if (!lightRecipe?.id && hasData) {
            handleAlert({
                title: i18n('spectrum_mix_no_light_recipe_title') as string,
                message: i18n('spectrum_mix_no_light_recipe_message') as string,
                okText: 'Yes',
                cancelText: 'No',
                okCallback: async () => {
                    const result = await findOrCreate();
                    if (result) {
                        pendingLightRecipeIdRef.current = result.recipe.id;
                        assignRecipe(result.recipe);
                    }
                    onSave();
                    pendingLightRecipeIdRef.current = undefined;
                },
                cancelCallback: () => onSave()
            });
        } else {
            onSave();
        }
    }

    if (!formData.loaded || !channelsLoaded) {
        return null;
    }

    if (groupHasNoChannels) {
        return (
            <Alert severity="info" sx={{ m: 2 }}>
                {i18n('spectrum_mixer_no_light_in_zone')}
            </Alert>
        );
    }

    return <SpectrumMixerView onSave={handleSave} onClose={mainMenu} />;
};

export default SpectrumMixer;
